/**
 * PulseSurveyPage — Employee takes weekly pulse survey
 * Route: /surveys (add to ALL roles — employees see their assigned surveys)
 *
 * Also usable for admins to view survey results.
 * Add to App.js (already has /surveys → SurveysPage — replace or combine)
 */

import React, { useState, useEffect } from 'react';
import {
    Container, Card, Row, Col, Button,
    Form, Badge, Spinner, Alert, ProgressBar,
} from 'react-bootstrap';
import { MessageSquare, CheckCircle, Star, ArrowRight, BarChart2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';

const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];

// ── Employee: take a survey ───────────────────────────────────────────────────
const TakeSurvey = ({ survey, onDone }) => {
    const [answers, setAnswers] = useState({});
    const [saving,  setSaving]  = useState(false);

    const setAnswer = (questionId, value) =>
        setAnswers(p => ({ ...p, [questionId]: value }));

    const submit = async () => {
        const questions = survey.questions || [];
        const unanswered = questions.filter(q => q.is_required && !answers[q.id]);
        if (unanswered.length) {
            toast.error(`Please answer all required questions (${unanswered.length} remaining)`);
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.post(`/surveys/${survey.id}/respond`, {
                answers: Object.entries(answers).map(([question_id, answer]) => ({
                    question_id: parseInt(question_id), answer,
                })),
            });
            toast.success('Survey submitted! Thank you for your feedback.');
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Submission failed');
        } finally { setSaving(false); }
    };

    const questions = survey.questions || [];

    return (
        <Card className="border-0 shadow-sm p-4">
            <div className="mb-4">
                <Badge bg="primary" className="mb-2" style={{ fontSize:10 }}>
                    {survey.type === 'pulse' ? '⚡ Weekly Pulse' : '📋 Survey'}
                </Badge>
                <h5 className="fw-bold mb-1">{survey.title}</h5>
                <p className="text-muted small mb-0">{survey.description}</p>
                {survey.closes_at && (
                    <div className="text-muted small mt-1">
                        Closes: {new Date(survey.closes_at).toLocaleDateString('en-IN')}
                    </div>
                )}
            </div>

            <ProgressBar
                now={Math.round((Object.keys(answers).length / Math.max(questions.length, 1)) * 100)}
                label={`${Object.keys(answers).length}/${questions.length}`}
                variant="primary"
                className="mb-4"
                style={{ height:6, borderRadius:10 }}
            />

            {questions.map((q, i) => (
                <div key={q.id} className="mb-4 pb-4"
                    style={{ borderBottom: i < questions.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div className="fw-bold mb-2 small d-flex gap-2">
                        <span style={{
                            background:'#eef2ff', color:'#4f46e5',
                            borderRadius:6, padding:'1px 8px', fontSize:11,
                        }}>
                            {i + 1}
                        </span>
                        {q.question_text}
                        {q.is_required && <span className="text-danger">*</span>}
                    </div>

                    {/* NPS / Rating 1–10 */}
                    {(q.type === 'nps' || q.type === 'rating') && (
                        <div className="d-flex gap-1 flex-wrap mt-2">
                            {Array.from({ length: q.type === 'nps' ? 10 : 5 }, (_, j) => j + 1).map(n => (
                                <button key={n}
                                    onClick={() => setAnswer(q.id, n)}
                                    style={{
                                        width:40, height:40, borderRadius:8,
                                        border: answers[q.id] === n
                                            ? '2px solid #4f46e5'
                                            : '1px solid #e5e7eb',
                                        background: answers[q.id] === n ? '#4f46e5' : '#fff',
                                        color: answers[q.id] === n ? '#fff' : '#374151',
                                        cursor:'pointer', fontWeight:600, fontSize:14,
                                        transition:'all 0.15s',
                                    }}>
                                    {n}
                                </button>
                            ))}
                            {q.type === 'nps' && (
                                <div className="d-flex justify-content-between w-100 mt-1">
                                    <span style={{ fontSize:10, color:'#94a3b8' }}>Not at all</span>
                                    <span style={{ fontSize:10, color:'#94a3b8' }}>Extremely</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Star rating */}
                    {q.type === 'stars' && (
                        <div className="d-flex gap-2 mt-2">
                            {[1,2,3,4,5].map(n => (
                                <Star key={n} size={28}
                                    fill={answers[q.id] >= n ? '#f59e0b' : 'none'}
                                    color={answers[q.id] >= n ? '#f59e0b' : '#d1d5db'}
                                    style={{ cursor:'pointer' }}
                                    onClick={() => setAnswer(q.id, n)}/>
                            ))}
                        </div>
                    )}

                    {/* Yes/No */}
                    {q.type === 'boolean' && (
                        <div className="d-flex gap-2 mt-2">
                            {['Yes','No'].map(opt => (
                                <Button key={opt} size="sm"
                                    variant={answers[q.id] === opt ? 'primary' : 'outline-secondary'}
                                    onClick={() => setAnswer(q.id, opt)}>
                                    {opt}
                                </Button>
                            ))}
                        </div>
                    )}

                    {/* Multiple choice */}
                    {q.type === 'mcq' && q.options && (
                        <div className="d-flex flex-column gap-2 mt-2">
                            {(typeof q.options === 'string'
                                ? JSON.parse(q.options)
                                : q.options
                            ).map((opt, oi) => (
                                <div key={oi}
                                    onClick={() => setAnswer(q.id, opt)}
                                    style={{
                                        padding:'10px 14px', borderRadius:8, cursor:'pointer',
                                        border: answers[q.id] === opt
                                            ? '2px solid #4f46e5'
                                            : '1px solid #e5e7eb',
                                        background: answers[q.id] === opt ? '#eef2ff' : '#fff',
                                        fontSize:13, transition:'all 0.15s',
                                    }}>
                                    {opt}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Text */}
                    {(q.type === 'text' || q.type === 'textarea') && (
                        <Form.Control as="textarea" rows={3} className="mt-2"
                            placeholder="Type your answer here…"
                            value={answers[q.id] || ''}
                            onChange={e => setAnswer(q.id, e.target.value)}
                            style={{ fontSize:13 }}/>
                    )}
                </div>
            ))}

            <Button variant="primary" onClick={submit} disabled={saving}
                className="w-100 rounded-pill py-3 fw-bold mt-2"
                style={{ background:'linear-gradient(135deg,#4f46e5,#7c3aed)', border:'none' }}>
                {saving ? <Spinner size="sm"/> : 'Submit Survey →'}
            </Button>
        </Card>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const PulseSurveyPage = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);
    const [tab,         setTab]        = useState('pending');
    const [pending,     setPending]    = useState([]);
    const [completed,   setCompleted]  = useState([]);
    const [loading,     setLoading]    = useState(true);
    const [taking,      setTaking]     = useState(null); // survey with questions
    const [surveyLoading, setSurveyLoading] = useState(false);
    const [done,        setDone]       = useState(false);

    const load = () => {
        setLoading(true);
        Promise.allSettled([
            axiosInstance.get('/surveys/my/pending'),
            axiosInstance.get('/surveys/my/completed'),
        ]).then(([pRes, cRes]) => {
            setPending(pRes.status === 'fulfilled' ? (pRes.value.data.data || []) : []);
            setCompleted(cRes.status === 'fulfilled' ? (cRes.value.data.data || []) : []);
        }).finally(() => setLoading(false));
    };

    useEffect(load, []);

    const openSurvey = async (surveyId) => {
        setSurveyLoading(true);
        setDone(false);
        try {
            const res = await axiosInstance.get(`/surveys/${surveyId}`);
            setTaking(res.data.data);
        } catch { toast.error('Failed to load survey'); }
        finally { setSurveyLoading(false); }
    };

    const onSurveyDone = () => {
        setTaking(null);
        setDone(true);
        load();
    };

    return (
        <Container className="py-4" style={{ maxWidth:700 }}>
            <div className="d-flex align-items-center gap-2 mb-4">
                <MessageSquare size={22} className="text-primary"/>
                <div>
                    <h4 className="fw-bold mb-0">Surveys & Pulse</h4>
                    <p className="text-muted small mb-0">
                        Your weekly pulse surveys and company feedback forms
                    </p>
                </div>
            </div>

            {done && (
                <Alert variant="success" className="d-flex align-items-center gap-2 mb-4">
                    <CheckCircle size={18}/>
                    <div>
                        <strong>Thank you!</strong> Your response has been recorded anonymously.
                    </div>
                </Alert>
            )}

            {taking ? (
                <>
                    <Button size="sm" variant="outline-secondary" className="mb-3"
                        onClick={() => setTaking(null)}>
                        ← Back to list
                    </Button>
                    <TakeSurvey survey={taking} onDone={onSurveyDone}/>
                </>
            ) : (
                <>
                    <div className="d-flex gap-2 mb-4">
                        <Button size="sm"
                            variant={tab==='pending' ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab('pending')}>
                            Pending
                            {pending.length > 0 && (
                                <Badge bg="danger" className="ms-1" style={{ fontSize:9 }}>
                                    {pending.length}
                                </Badge>
                            )}
                        </Button>
                        <Button size="sm"
                            variant={tab==='done' ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab('done')}>
                            Completed
                        </Button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary"/>
                        </div>
                    ) : tab === 'pending' ? (
                        pending.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <CheckCircle size={40} className="mb-3 opacity-30"/>
                                <p>All caught up! No pending surveys.</p>
                                <p className="small">New pulse surveys arrive every Monday morning.</p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {pending.map((s, i) => (
                                    <Card key={i} className="border-0 shadow-sm">
                                        <Card.Body className="p-4 d-flex justify-content-between align-items-center gap-3 flex-wrap">
                                            <div>
                                                <Badge bg={s.type==='pulse' ? 'primary' : 'info'}
                                                    style={{ fontSize:10, marginBottom:6, display:'block', width:'fit-content' }}>
                                                    {s.type==='pulse' ? '⚡ Weekly Pulse' : '📋 Survey'}
                                                </Badge>
                                                <h6 className="fw-bold mb-1">{s.title}</h6>
                                                <p className="text-muted small mb-0">{s.description}</p>
                                                {s.closes_at && (
                                                    <div className="text-muted" style={{ fontSize:11, marginTop:4 }}>
                                                        Closes {new Date(s.closes_at).toLocaleDateString('en-IN')}
                                                    </div>
                                                )}
                                            </div>
                                            <Button variant="primary" size="sm"
                                                className="rounded-pill px-4"
                                                disabled={surveyLoading}
                                                onClick={() => openSurvey(s.id)}>
                                                {surveyLoading ? <Spinner size="sm"/> : <>Take Survey <ArrowRight size={13}/></>}
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )
                    ) : (
                        completed.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No completed surveys yet.
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {completed.map((s, i) => (
                                    <Card key={i} className="border-0 shadow-sm">
                                        <Card.Body className="p-4 d-flex justify-content-between align-items-center gap-3">
                                            <div>
                                                <Badge bg="success" style={{ fontSize:10, marginBottom:4, display:'block', width:'fit-content' }}>
                                                    ✓ Completed
                                                </Badge>
                                                <h6 className="fw-bold mb-1">{s.title}</h6>
                                                <div className="text-muted small">
                                                    Submitted {s.submitted_at
                                                        ? new Date(s.submitted_at).toLocaleDateString('en-IN')
                                                        : '—'}
                                                </div>
                                            </div>
                                            <CheckCircle size={22} color="#059669"/>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )
                    )}
                </>
            )}
        </Container>
    );
};

export default PulseSurveyPage;
